import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaChartLine, FaFileInvoiceDollar, FaRoute, FaCheckCircle, FaCalendarAlt,
  FaBox, FaClipboardList, FaFileSignature, FaPrint, FaFileCsv, FaSpinner
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { getCompanyDetails } from '../config/CompanyDetails';
import { clientReports } from '../api';

// ── formatting ──────────────────────────────────────────────────────────────
const money = (n) => (n === null || n === undefined ? '—' : 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const money0 = (n) => (n === null || n === undefined ? '—' : 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 }));
const nr = (n, dp = 0) => (n === null || n === undefined ? '—' : (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const pct = (n) => (n === null || n === undefined ? '—' : `${n}%`);
const date = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dateTime = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const titleize = (v) => (v ? String(v).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

const REPORTS = [
  { key: 'summary', label: 'Executive Summary', icon: <FaChartLine size={13} /> },
  { key: 'statement', label: 'Statement of Account', icon: <FaFileInvoiceDollar size={13} /> },
  { key: 'lanes', label: 'Cost per Lane', icon: <FaRoute size={13} /> },
  { key: 'deliveries', label: 'Deliveries & PODs', icon: <FaCheckCircle size={13} /> },
  { key: 'trend', label: 'Volume & Spend Trend', icon: <FaCalendarAlt size={13} /> },
  { key: 'commodities', label: 'Commodities', icon: <FaBox size={13} /> },
  { key: 'register', label: 'Load Register', icon: <FaClipboardList size={13} /> },
  { key: 'quotes', label: 'Quote Activity', icon: <FaFileSignature size={13} /> }
];

const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'last12', label: 'Last 12 months' },
  { key: 'custom', label: 'Custom' }
];

const iso = (d) => d.toISOString().slice(0, 10);

const periodRange = (key) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (key === 'this_month') return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  if (key === 'last_month') return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (key === 'this_quarter') {
    const q = Math.floor(m / 3);
    return { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) };
  }
  if (key === 'ytd') return { from: `${y}-01-01`, to: iso(now) };
  if (key === 'last12') return { from: iso(new Date(y, m - 11, 1)), to: iso(now) };
  return null;
};

// ── CSV ─────────────────────────────────────────────────────────────────────
const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (filename, headers, rows) => {
  const lines = [headers.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))];
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ── building blocks ─────────────────────────────────────────────────────────
const Stat = ({ label, value, sub, tone }) => (
  <div className="rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
    <p className="text-xs uppercase tracking-wide" style={{ color: '#94A3B8' }}>{label}</p>
    <p className="text-xl font-bold mt-1" style={{ color: tone || '#0A1929' }}>{value}</p>
    {sub ? <p className="text-xs mt-1" style={{ color: '#4A5568' }}>{sub}</p> : null}
  </div>
);

const Table = ({ headers, rows, empty }) => (
  rows && rows.length > 0 ? (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider" style={{ color: '#94A3B8' }}>
          <tr className="border-b" style={{ borderColor: '#E8ECEF' }}>
            {headers.map((h, i) => (
              <th key={i} className={`py-2 pr-4 whitespace-nowrap ${h.right ? 'text-right' : ''}`}>{h.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b" style={{ borderColor: '#F1F5F9' }}>
              {r.map((cell, j) => (
                <td key={j} className={`py-2 pr-4 align-top ${headers[j] && headers[j].right ? 'text-right whitespace-nowrap' : ''}`} style={{ color: '#0A1929' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p className="text-sm py-6 text-center" style={{ color: '#94A3B8' }}>{empty || 'Nothing to show for this period yet.'}</p>
  )
);

const Bars = ({ items, valueKey = 'value', labelKey = 'label', max = 8, format }) => {
  const rows = (items || []).slice(0, max);
  if (rows.length === 0) return null;
  const top = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  const fmt = format || money0;
  return (
    <div className="space-y-3">
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-xs mb-1" style={{ color: '#4A5568' }}>
            <span className="truncate pr-3">{r[labelKey]}</span>
            <span className="font-semibold whitespace-nowrap" style={{ color: '#0A1929' }}>{fmt(r[valueKey])}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#F1F5F9' }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max(2, Math.round(((Number(r[valueKey]) || 0) / top) * 100))}%`, backgroundColor: '#00A86B' }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const rateCell = (row) => (row.basis === 'rate' && row.rate_per_ton !== null
  ? money(row.rate_per_ton)
  : (row.basis === 'lump' ? 'Lump sum' : '—'));

const ReportHeader = ({ company, client, period, reportTitle, generatedAt }) => (
  <div className="mb-5 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-xs overflow-hidden" style={{ backgroundColor: '#0A1929', color: '#00A86B' }}>
          {company && company.company_logo_url ? (
            <img src={company.company_logo_url} alt="One Purpose Logistics" className="w-9 h-9 object-contain" />
          ) : 'OV'}
        </div>
        <div>
          <p className="font-bold" style={{ color: '#0A1929' }}>
            ONE<span style={{ color: '#00A86B' }}>VIEW</span>
            <span className="font-normal text-sm" style={{ color: '#4A5568' }}> · One Purpose Logistics</span>
          </p>
          <p className="text-xs" style={{ color: '#4A5568' }}>
            {[company && company.company_phone, company && company.company_email, company && company.company_website].filter(Boolean).join(' · ') || 'onepurposelogistics.com'}
          </p>
        </div>
      </div>
      <div className="text-right text-xs" style={{ color: '#4A5568' }}>
        <p className="font-semibold text-sm" style={{ color: '#0A1929' }}>{reportTitle}</p>
        {period ? <p>Period: {date(period.from)} to {date(period.to)} ({nr(period.days)} days)</p> : null}
        <p>Generated by One Purpose Logistics on {dateTime(generatedAt)}</p>
      </div>
    </div>
    {client ? (
      <p className="text-xs mt-3" style={{ color: '#4A5568' }}>
        <span className="font-semibold" style={{ color: '#0A1929' }}>{client.company_name}</span>
        {client.registration_number ? ` · Reg ${client.registration_number}` : ''}
        {client.vat_number ? ` · VAT ${client.vat_number}` : ''}
        {client.address ? ` · ${client.address}` : ''}
      </p>
    ) : null}
  </div>
);

const ReportFooter = ({ scope, generatedAt }) => (
  <div className="mt-6 pt-4 border-t text-xs" style={{ borderColor: '#E8ECEF', color: '#94A3B8' }}>
    <p>
      Report generated by <strong style={{ color: '#4A5568' }}>OneView — One Purpose Logistics</strong> on {dateTime(generatedAt)}.
      Figures are drawn live from your account at the time of printing.
    </p>
    {scope ? (
      <p className="mt-1">
        Basis: loads counted by {scope.loads_by}; invoices by {scope.invoices_by}; quotes by {scope.quotes_by}.
        Work value uses {scope.value_basis}.
      </p>
    ) : null}
  </div>
);

// ── CSV layouts, one per report ─────────────────────────────────────────────
const csvFor = (report, data) => {
  if (!data) return null;
  const strip = (d) => date(d);
  if (report === 'summary') {
    const k = data.kpi || {};
    return {
      name: 'executive-summary',
      headers: ['Metric', 'Value'],
      rows: [
        ['Period', `${data.period.from} to ${data.period.to}`],
        ['Loads in period', k.loads_total],
        ['Loads delivered', k.loads_delivered],
        ['Loads in transit', k.loads_live],
        ['Tons delivered', k.tons_delivered],
        ['Distance delivered (km)', k.distance_km],
        ['Value of work (excl VAT)', k.work_value],
        ['Invoiced (incl VAT)', k.invoiced_total],
        ['VAT', k.vat_total],
        ['Paid', k.paid_total],
        ['Credited', k.credited_total],
        ['Outstanding', k.outstanding_total],
        ['Avg rate per ton (rated work)', k.avg_rate_per_ton],
        ['Avg cost per km', k.avg_cost_per_km],
        ['On-time rate %', k.on_time_rate],
        ['POD completeness %', k.pod_completeness],
        ['Avg transit hours', k.avg_transit_hours]
      ]
    };
  }
  if (report === 'statement') {
    return {
      name: 'statement-of-account',
      headers: ['Invoice', 'Date', 'Due', 'Job', 'Total', 'VAT', 'Paid', 'Credited', 'Balance', 'Days overdue', 'Status'],
      rows: (data.statement.rows || []).map((r) => [r.number, r.date, r.due_date, r.job_number, r.total, r.vat, r.paid, r.credited, r.balance, r.days_overdue, r.status])
    };
  }
  if (report === 'lanes') {
    return {
      name: 'cost-per-lane',
      headers: ['From', 'To', 'Distance km', 'Loads', 'Delivered', 'Tons', 'Value', 'Rate per ton', 'Cost per km', 'On-time %'],
      rows: (data.lanes || []).map((l) => [l.from, l.to, l.distance_km, l.loads, l.delivered, l.tons, l.value, l.basis === 'rate' ? l.rate_per_ton : 'Lump sum', l.cost_per_km, l.on_time_rate])
    };
  }
  if (report === 'deliveries') {
    return {
      name: 'deliveries-and-pods',
      headers: ['Load', 'Job', 'Route', 'Scheduled pickup', 'Departed', 'Arrived', 'Offloaded', 'Transit hours', 'Tons', 'On time', 'Delay reason', 'POD number', 'POD status', 'Receiver'],
      rows: (data.deliveries || []).map((d) => [d.load_number, d.job_number, d.route, d.scheduled_pickup, d.departed, d.arrived, d.offloaded, d.transit_hours, d.weight_mt, d.on_time_status, d.delay_reason, d.pod_number, d.pod_status, d.receiver])
    };
  }
  if (report === 'trend') {
    return {
      name: 'volume-and-spend-trend',
      headers: ['Month', 'Loads', 'Delivered', 'Tons', 'Value', 'Rate per ton', 'On-time %'],
      rows: (data.months || []).map((m) => [m.label, m.loads, m.delivered, m.tons, m.value, m.basis === 'rate' ? m.rate_per_ton : 'Lump sum', m.on_time_rate])
    };
  }
  if (report === 'commodities') {
    return {
      name: 'commodities',
      headers: ['Commodity', 'Loads', 'Tons delivered', 'Value', 'Rate per ton', 'Share %'],
      rows: (data.commodities || []).map((c) => [c.commodity, c.loads, c.delivered_tons, c.value, c.basis === 'rate' ? c.rate_per_ton : 'Lump sum', c.share_pct])
    };
  }
  if (report === 'register') {
    return {
      name: 'load-register',
      headers: ['Load', 'Job', 'Date', 'Route', 'Commodity', 'Tons', 'Km', 'Truck', 'Status', 'POD', 'POD status', 'Receiver'],
      rows: (data.registers || []).map((r) => [r.load_number, r.job_number, strip(r.date), r.route, r.commodity, r.tons, r.distance_km, r.truck, titleize(r.status), r.pod_number, titleize(r.pod_status), r.receiver])
    };
  }
  if (report === 'quotes') {
    return {
      name: 'quote-activity',
      headers: ['Quote', 'Date', 'Type', 'Route', 'Value', 'Status', 'Valid until', 'Job'],
      rows: (data.quotes || []).map((q) => [q.quote_number, strip(q.date), titleize(q.quote_type), [q.origin, q.destination].filter(Boolean).join(' -> '), q.value, titleize(q.status), q.expiry_date, q.job_number])
    };
  }
  return null;
};

function ClientReports({ onLogout }) {
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [company, setCompany] = useState(null);
  const [report, setReport] = useState('summary');
  const [periodKey, setPeriodKey] = useState('ytd');
  const [range, setRange] = useState(() => periodRange('ytd'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getCompanyDetails().then((c) => setCompany(c)).catch(() => { /* the header falls back to text */ });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await clientReports.pack({ from: range.from, to: range.to });
        if (cancelled) return;
        if (res && res.success) setData(res);
        else setError((res && res.error) || 'Could not build your reports.');
      } catch (err) {
        console.error('Reports error:', err);
        if (!cancelled) setError('Could not build your reports. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [range]);

  const pickPeriod = (key) => {
    setPeriodKey(key);
    const r = periodRange(key);
    if (r) setRange(r);
  };

  const exportCsv = () => {
    const spec = csvFor(report, data);
    if (!spec) return;
    downloadCsv(`OneView-${spec.name}-${range.from}-to-${range.to}.csv`, spec.headers, spec.rows);
    toast.success('CSV downloaded');
  };

  const activeReport = REPORTS.find((r) => r.key === report) || REPORTS[0];
  const k = (data && data.kpi) || {};

  return (
    <ClientLayout activeTab="reports" onLogout={onLogout} user={user}>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #report-canvas, #report-canvas * { visibility: visible !important; }
          #report-canvas { position: absolute; left: 0; top: 0; width: 100%; border: none !important; padding: 0 8mm !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="flex flex-wrap justify-between items-start gap-4 mb-5 no-print">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📊 Reports</h1>
          <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
            Professional reports on your transport spend, service levels and volumes. Print one or download the data.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={!data}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
          >
            <FaFileCsv size={13} /> CSV
          </button>
          <button
            onClick={() => window.print()}
            disabled={!data}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#00A86B' }}
          >
            <FaPrint size={13} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-4 mb-5 no-print" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-wide mr-1" style={{ color: '#94A3B8' }}>Period</span>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => pickPeriod(p.key)}
              className="px-3 py-1.5 rounded-full border text-xs font-medium"
              style={{
                borderColor: periodKey === p.key ? '#00A86B' : '#E8ECEF',
                backgroundColor: periodKey === p.key ? '#f0fdf4' : '#ffffff',
                color: periodKey === p.key ? '#047857' : '#4A5568'
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-xs flex items-center gap-2" style={{ color: '#4A5568' }}>
            From
            <input
              type="date"
              value={range.from}
              onChange={(e) => { setPeriodKey('custom'); setRange((r) => ({ ...r, from: e.target.value })); }}
              className="px-2 py-1 border rounded-lg text-sm"
              style={{ borderColor: '#E8ECEF' }}
            />
          </label>
          <label className="text-xs flex items-center gap-2" style={{ color: '#4A5568' }}>
            To
            <input
              type="date"
              value={range.to}
              onChange={(e) => { setPeriodKey('custom'); setRange((r) => ({ ...r, to: e.target.value })); }}
              className="px-2 py-1 border rounded-lg text-sm"
              style={{ borderColor: '#E8ECEF' }}
            />
          </label>
          {data && data.period ? (
            <span className="text-xs" style={{ color: '#94A3B8' }}>{nr(data.period.days)} days</span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {REPORTS.map((r) => (
            <button
              key={r.key}
              onClick={() => setReport(r.key)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold"
              style={{
                borderColor: report === r.key ? '#0A1929' : '#E8ECEF',
                backgroundColor: report === r.key ? '#0A1929' : '#ffffff',
                color: report === r.key ? '#ffffff' : '#4A5568'
              }}
            >
              {r.icon} {r.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border p-4 mb-6 text-sm" style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2', color: '#b91c1c' }}>{error}</div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center h-64 no-print">
          <div className="text-center">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-4" style={{ color: '#00A86B' }} />
            <p className="text-gray-500">Building your reports...</p>
          </div>
        </div>
      ) : data ? (
        <div id="report-canvas" className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
          <ReportHeader
            company={company}
            client={data.client}
            period={data.period}
            reportTitle={activeReport.label}
            generatedAt={data.generated_at}
          />
          {report === 'summary' && (
            <div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <Stat
                  label="Loads delivered"
                  value={nr(k.loads_delivered)}
                  sub={`${nr(k.loads_total)} in period${k.loads_live ? ` · ${k.loads_live} still in transit` : ''}`}
                />
                <Stat
                  label="Tons delivered"
                  value={`${nr(k.tons_delivered, 1)} MT`}
                  sub={k.tons_planned ? `${nr(k.tons_planned, 1)} MT planned on those jobs` : 'Weight from the delivery notes'}
                />
                <Stat
                  label="Value of work (excl VAT)"
                  value={money0(k.work_value)}
                  sub={k.avg_rate_per_ton ? `Avg ${money(k.avg_rate_per_ton)} per ton on rated work` : 'Based on your agreed rates'}
                />
                <Stat
                  label="Invoiced (incl VAT)"
                  value={money0(k.invoiced_total)}
                  sub={`${money0(k.paid_total)} paid · ${money0(k.outstanding_total)} open`}
                  tone={k.outstanding_total > 0 ? '#b45309' : '#16a34a'}
                />
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat
                  label="Distance delivered"
                  value={`${nr(k.distance_km)} km`}
                  sub={k.avg_cost_per_km ? `Avg ${money(k.avg_cost_per_km)} per km` : '—'}
                />
                <Stat
                  label="On-time deliveries"
                  value={pct(k.on_time_rate)}
                  sub={k.on_time_sample ? `${k.on_time_count} of ${k.on_time_sample} measured` : 'Needs delivery timestamps'}
                  tone={k.on_time_rate === null ? undefined : (k.on_time_rate >= 90 ? '#16a34a' : (k.on_time_rate >= 75 ? '#b45309' : '#dc2626'))}
                />
                <Stat
                  label="POD completeness"
                  value={pct(k.pod_completeness)}
                  sub={k.pod_outstanding ? `${k.pod_outstanding} still outstanding` : 'Delivered loads signed off'}
                />
                <Stat
                  label="Average transit time"
                  value={k.avg_transit_hours !== null ? `${nr(k.avg_transit_hours, 1)} h` : '—'}
                  sub="Pickup departure to offload"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3 text-sm" style={{ color: '#0A1929' }}>Where the money went (by lane)</h4>
                  {(data.lanes || []).length > 0 ? (
                    <Bars items={data.lanes.map((l) => ({ label: `${l.from} → ${l.to}`, value: l.value }))} />
                  ) : (
                    <p className="text-sm py-4" style={{ color: '#94A3B8' }}>No loads fell inside this period.</p>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold mb-3 text-sm" style={{ color: '#0A1929' }}>What needs your attention</h4>
                  <ul className="space-y-2 text-sm" style={{ color: '#4A5568' }}>
                    {k.quotes_open > 0 ? <li>• {k.quotes_open} quote(s) waiting for your decision — {money0(k.quotes_value_open)}</li> : null}
                    {k.outstanding_total > 0 ? <li>• {money0(k.outstanding_total)} outstanding on your account</li> : null}
                    {k.pod_outstanding > 0 ? <li>• {k.pod_outstanding} proof(s) of delivery outstanding</li> : null}
                    {k.late_count > 0 ? <li>• {k.late_count} late delivery(ies) in this period</li> : null}
                    {k.late_count === 0 && k.outstanding_total === 0 && k.quotes_open === 0 && k.pod_outstanding === 0 ? (
                      <li>• Nothing outstanding — everything is up to date.</li>
                    ) : null}
                  </ul>
                  <h4 className="font-semibold mb-2 mt-5 text-sm" style={{ color: '#0A1929' }}>Top commodities</h4>
                  {(data.commodities || []).length > 0 ? (
                    <Table
                      headers={[{ label: 'Commodity' }, { label: 'Loads', right: true }, { label: 'Tons', right: true }, { label: 'Value', right: true }]}
                      rows={data.commodities.slice(0, 5).map((c) => [c.commodity, nr(c.loads), nr(c.delivered_tons, 1), money0(c.value)])}
                    />
                  ) : (
                    <p className="text-sm" style={{ color: '#94A3B8' }}>No commodities captured in this period.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {report === 'statement' && (
            <div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat
                  label="Invoiced in period (incl VAT)"
                  value={money0(data.statement.totals.invoiced_period)}
                  sub={`VAT ${money0(data.statement.totals.vat_period)}`}
                />
                <Stat label="Paid in period" value={money0(data.statement.totals.paid_period)} tone="#16a34a" />
                <Stat
                  label="Outstanding on account"
                  value={money0(data.statement.totals.outstanding_all)}
                  tone={data.statement.totals.outstanding_all > 0 ? '#b45309' : '#16a34a'}
                  sub={`${data.statement.rows.filter((r) => r.balance > 0).length} open invoice(s)`}
                />
                <Stat label="Credited to date" value={money0(data.statement.totals.credited_all)} />
              </div>

              <h4 className="font-semibold mb-3 text-sm" style={{ color: '#0A1929' }}>Ageing of your account</h4>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <Stat label="Current" value={money0(data.statement.ageing.current)} />
                <Stat label="1 - 30 days" value={money0(data.statement.ageing.d30)} tone="#b45309" />
                <Stat label="31 - 60 days" value={money0(data.statement.ageing.d60)} tone="#b45309" />
                <Stat label="61 - 90 days" value={money0(data.statement.ageing.d90)} tone="#dc2626" />
                <Stat label="90+ days" value={money0(data.statement.ageing.d90plus)} tone="#dc2626" />
              </div>

              <Table
                headers={[
                  { label: 'Invoice' }, { label: 'Date' }, { label: 'Due' }, { label: 'Job' },
                  { label: 'Total', right: true }, { label: 'VAT', right: true }, { label: 'Paid', right: true },
                  { label: 'Credited', right: true }, { label: 'Balance', right: true }, { label: 'Overdue', right: true },
                  { label: 'Status' }
                ]}
                rows={data.statement.rows.map((r) => [
                  r.number, date(r.date), date(r.due_date), r.job_number || '—',
                  money(r.total), money(r.vat), money(r.paid), money(r.credited), money(r.balance),
                  r.days_overdue > 0 ? `${r.days_overdue} d` : '—', titleize(r.status)
                ])}
                empty="No invoices have been issued to you yet."
              />
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                Agreed payment terms: {data.client.payment_terms || 'as per your agreement'}. Proformas are excluded because they are not payable.
              </p>
            </div>
          )}

          {report === 'lanes' && (
            <div>
              <Table
                headers={[
                  { label: 'From' }, { label: 'To' }, { label: 'Distance', right: true },
                  { label: 'Loads', right: true }, { label: 'Delivered', right: true },
                  { label: 'Tons', right: true }, { label: 'Value', right: true },
                  { label: 'Rate per ton', right: true }, { label: 'Cost per km', right: true },
                  { label: 'On-time', right: true }
                ]}
                rows={(data.lanes || []).map((l) => [
                  l.from,
                  <span key="to">{l.to}{l.cross_border ? ' · Cross-border' : ''}</span>,
                  l.distance_km ? `${nr(l.distance_km)} km` : '—',
                  nr(l.loads),
                  nr(l.delivered),
                  nr(l.tons, 1),
                  money0(l.value),
                  rateCell(l),
                  l.cost_per_km !== null ? money(l.cost_per_km) : '—',
                  pct(l.on_time_rate)
                ])}
                empty="No lanes ran in this period yet. Once you have loads on the road they appear here with your cost per ton and per kilometre."
              />
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                Use this to compare rates between routes and to budget the next movement. Lump-sum work (abnormal loads) is shown as a lump sum instead of a rate per ton.
              </p>
            </div>
          )}

          {report === 'deliveries' && (
            <div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat label="Delivered loads" value={nr(k.loads_delivered)} sub={`${nr(k.pod_received)} with a POD on file`} />
                <Stat
                  label="On-time deliveries"
                  value={pct(k.on_time_rate)}
                  sub={k.on_time_sample ? `${k.on_time_count} on time · ${k.late_count} late` : 'Needs delivery timestamps'}
                  tone={k.on_time_rate === null ? undefined : (k.on_time_rate >= 90 ? '#16a34a' : (k.on_time_rate >= 75 ? '#b45309' : '#dc2626'))}
                />
                <Stat
                  label="Average transit time"
                  value={k.avg_transit_hours !== null ? `${nr(k.avg_transit_hours, 1)} h` : '—'}
                  sub="Departure to offload"
                />
                <Stat
                  label="POD completeness"
                  value={pct(k.pod_completeness)}
                  sub={k.pod_outstanding ? `${k.pod_outstanding} outstanding` : 'All signed off'}
                />
              </div>

              <Table
                headers={[
                  { label: 'Load' }, { label: 'Job' }, { label: 'Route' },
                  { label: 'Scheduled pickup' }, { label: 'Departed' }, { label: 'Offloaded' },
                  { label: 'Transit', right: true }, { label: 'Tons', right: true },
                  { label: 'On time' }, { label: 'Delay reason' }, { label: 'POD' }, { label: 'POD status' }, { label: 'Received by' }
                ]}
                rows={(data.deliveries || []).map((d) => [
                  d.load_number || '—', d.job_number || '—', d.route,
                  dateTime(d.scheduled_pickup), dateTime(d.departed), dateTime(d.offloaded),
                  d.transit_hours !== null ? `${nr(d.transit_hours, 1)} h` : '—', nr(d.weight_mt, 1),
                  d.on_time_status ? titleize(d.on_time_status) : '—', d.delay_reason || '—',
                  d.pod_number || '—', d.pod_status ? titleize(d.pod_status) : '—', d.receiver || '—'
                ])}
                empty="No deliveries in this period yet. This report becomes your audit trail as soon as loads start running."
              />
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                Planned versus actual times, any delay reason, and whether the signed proof of delivery is on file.
              </p>
            </div>
          )}

          {report === 'trend' && (
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-semibold mb-3 text-sm" style={{ color: '#0A1929' }}>Value of work by month</h4>
                  <Bars items={(data.months || []).map((m) => ({ label: m.label, value: m.value }))} max={12} />
                </div>
                <div>
                  <h4 className="font-semibold mb-3 text-sm" style={{ color: '#0A1929' }}>Tons delivered by month</h4>
                  <Bars
                    items={(data.months || []).map((m) => ({ label: m.label, value: m.delivered_tons }))}
                    max={12}
                    format={(v) => (v === null || v === undefined ? '—' : `${nr(v, 1)} MT`)}
                  />
                </div>
              </div>

              <Table
                headers={[
                  { label: 'Month' }, { label: 'Loads', right: true }, { label: 'Delivered', right: true },
                  { label: 'Tons', right: true }, { label: 'Value', right: true },
                  { label: 'Rate per ton', right: true }, { label: 'On-time', right: true }
                ]}
                rows={(data.months || []).map((m) => [
                  m.label, nr(m.loads), nr(m.delivered), nr(m.tons, 1), money0(m.value), rateCell(m), pct(m.on_time_rate)
                ])}
                empty="No history yet."
              />
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                The last twelve months, so you can see seasonality and whether your rates per ton are moving.
              </p>
            </div>
          )}

          {report === 'commodities' && (
            <div>
              <div className="mb-6">
                <h4 className="font-semibold mb-3 text-sm" style={{ color: '#0A1929' }}>Value by commodity</h4>
                <Bars items={(data.commodities || []).map((c) => ({ label: c.commodity, value: c.value }))} />
              </div>
              <Table
                headers={[
                  { label: 'Commodity' }, { label: 'Loads', right: true }, { label: 'Tons delivered', right: true },
                  { label: 'Value', right: true }, { label: 'Rate per ton', right: true }, { label: 'Share of value', right: true }
                ]}
                rows={(data.commodities || []).map((c) => [
                  c.commodity, nr(c.loads), nr(c.delivered_tons, 1), money0(c.value), rateCell(c), `${nr(c.share_pct)}%`
                ])}
                empty="No commodities captured in this period yet."
              />
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                Share is measured by value, not tonnage — useful when you move a mix of dense and light cargo.
              </p>
            </div>
          )}

          {report === 'register' && (
            <div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat label="Loads in register" value={nr((data.registers || []).length)} />
                <Stat label="Tons delivered" value={`${nr(k.tons_delivered, 1)} MT`} />
                <Stat label="Value of work" value={money0(k.work_value)} />
                <Stat
                  label="PODs on file"
                  value={nr(k.pod_received)}
                  sub={k.pod_outstanding ? `${k.pod_outstanding} outstanding` : 'All signed off'}
                />
              </div>

              <Table
                headers={[
                  { label: 'Load' }, { label: 'Job' }, { label: 'Date' }, { label: 'Route' }, { label: 'Commodity' },
                  { label: 'Tons', right: true }, { label: 'Km', right: true }, { label: 'Truck' },
                  { label: 'Status' }, { label: 'POD' }, { label: 'POD status' }, { label: 'Received by' }
                ]}
                rows={(data.registers || []).map((r) => [
                  r.load_number || '—', r.job_number || '—', date(r.date), r.route, r.commodity || '—',
                  nr(r.tons, 1), r.distance_km ? nr(r.distance_km) : '—', r.truck || '—',
                  titleize(r.status), r.pod_number || '—', r.pod_status ? titleize(r.pod_status) : '—', r.receiver || '—'
                ])}
                empty="No loads in this period yet. This becomes your movement register — one line per truck load."
              />
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                One line per truck load with its delivery note reference, ready to reconcile against your own records.
              </p>
            </div>
          )}

          {report === 'quotes' && (
            <div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat
                  label="Quotes issued"
                  value={nr((data.quotes_summary || {}).issued)}
                  sub={money0((data.quotes_summary || {}).issued_value)}
                />
                <Stat label="Accepted" value={nr((data.quotes_summary || {}).accepted)} tone="#16a34a" />
                <Stat label="Waiting on you" value={nr((data.quotes_summary || {}).open)} tone="#b45309" />
                <Stat label="Conversion" value={pct((data.quotes_summary || {}).conversion_pct)} sub="Accepted versus decided" />
              </div>

              <Table
                headers={[
                  { label: 'Quote' }, { label: 'Date' }, { label: 'Type' }, { label: 'Route' },
                  { label: 'Value', right: true }, { label: 'Status' }, { label: 'Valid until' }, { label: 'Job' }
                ]}
                rows={(data.quotes || []).map((q) => [
                  q.quote_number, date(q.date), titleize(q.quote_type),
                  [q.origin, q.destination].filter(Boolean).join(' → ') || '—',
                  money0(q.value), titleize(q.status), date(q.expiry_date), q.job_number || '—'
                ])}
                empty="No quotes were issued in this period."
              />
              <p className="text-xs mt-3" style={{ color: '#94A3B8' }}>
                Every quote we issued in the period, what it was worth and where it stands. Open quotes still need your decision on the quotes page.
              </p>
            </div>
          )}




          <ReportFooter scope={data.scope} generatedAt={data.generated_at} />
        </div>
      ) : null}
    </ClientLayout>
  );

}

export default ClientReports;



